import React, { useEffect, useState } from 'react';
import AppShell from '../components/AppShell';
import api from '../lib/api';
import { LoadingBlock, EmptyState } from '../components/States';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Trash2, Pencil, Tag } from 'lucide-react';
import { toast } from 'sonner';

import { errMsg } from '../lib/errors';
export default function Brands() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: '', description: '', logo: '', active: true });

    const load = async () => {
        setLoading(true);
        try { const r = await api.get('/brands'); setItems(r.data); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setForm({ name: '', description: '', logo: '', active: true }); setDialogOpen(true); };
    const openEdit = (b) => { setEditing(b); setForm({ name: b.name, description: b.description || '', logo: b.logo || '', active: b.active !== false }); setDialogOpen(true); };

    const save = async () => {
        try {
            if (editing) await api.patch(`/brands/${editing.id}`, form);
            else await api.post('/brands', form);
            toast.success('Saved'); setDialogOpen(false); load();
        } catch (e) { toast.error(errMsg(e, 'Save failed')); }
    };
    const doDelete = async (b) => {
        if (!window.confirm(`Delete brand "${b.name}"?`)) return;
        try { await api.delete(`/brands/${b.id}`); toast.success('Deleted'); load(); }
        catch (e) { toast.error(errMsg(e, 'Delete failed')); }
    };

    return (
        <AppShell pageTitle="Brands" pageActions={
            <Button onClick={openNew} data-testid="add-brand-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>
                <Plus className="h-4 w-4 mr-2" />Add Brand
            </Button>
        }>
            {loading ? <LoadingBlock /> : items.length === 0 ? (
                <EmptyState icon={Tag} title="No brands yet" description="Add your first brand." action={<Button onClick={openNew}><Plus className="h-4 w-4 mr-2" />Add Brand</Button>} />
            ) : (
                <Card className="p-0 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="brands-table">
                        <thead>
                            <tr className="border-b bg-neutral-50 text-xs text-neutral-500">
                                <th className="text-left p-3 font-medium">Name</th>
                                <th className="text-left font-medium">Description</th>
                                <th className="text-left font-medium">Status</th>
                                <th className="text-right p-3 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(b => (
                                <tr key={b.id} className="border-b hover:bg-neutral-50" data-testid={`brand-row-${b.slug}`}>
                                    <td className="p-3 font-medium">{b.name}</td>
                                    <td className="text-neutral-600">{b.description}</td>
                                    <td>{b.active ? <span className="text-green-700 text-xs font-medium">Active</span> : <span className="text-neutral-500 text-xs">Inactive</span>}</td>
                                    <td className="p-3 text-right">
                                        <Button variant="ghost" size="icon" onClick={() => openEdit(b)}><Pencil className="h-4 w-4" /></Button>
                                        <Button variant="ghost" size="icon" onClick={() => doDelete(b)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editing ? 'Edit Brand' : 'New Brand'}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="brand-name-input" /></div>
                        <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
                        <div><Label>Logo URL</Label><Input value={form.logo} onChange={e => setForm({ ...form, logo: e.target.value })} placeholder="https://..." /></div>
                        <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={v => setForm({ ...form, active: v })} /><Label>Active</Label></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button onClick={save} data-testid="brand-save-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}
